import React from "react"
import MyCard from "@common-utils/MyCard"
import MyTooltip from "@common-utils/MyTooltip"
import { Form, Formik, FormikHelpers } from "formik"
import moment from "moment/moment"
import { isEqual } from "lodash"
import { useConsultantsListingContext } from "./ConsultantsListing"
import { IGetConsultantsListingRequestModel } from "../core/_models"
import {
     CONST_CONSULTANT_CRA_DETAILS_ETAT_EN_MISSION,
     CONST_CONSULTANT_CRA_DETAILS_ETAT_INACTIF,
     CONST_CONSULTANT_STATUTS_OPTIONS_FOR_SELECT2,
     CONST_CONTRATS_FOR_SELECT2,
} from "@common-constants/*"
import { CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT, CONST_API_UTILS_GET_CLIENTS_ENDPOINT, CONST_API_UTILS_GET_ENTITES_ENDPOINT } from "../../../../endpoints"

// 🔹 New Field Components
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MySelectFormField from "@common-components/fields/MySelectFormField"
// Using old date field as no new one was provided, wrapped manually
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"

const ConsultantsListingFilters = () => {
     const context = useConsultantsListingContext()

     const initialValues: IGetConsultantsListingRequestModel = {
          ...context.filters,
     }

     function handleSubmit(values: IGetConsultantsListingRequestModel, helpers: FormikHelpers<IGetConsultantsListingRequestModel>) {
          const newFilters = { ...context.filters, ...values }

          // Optimization: Don't fetch if nothing changed
          if (isEqual(context.filters, newFilters)) {
               helpers.setSubmitting(false)
               if (window.innerWidth < 992) context.setAreFiltersVisible(false)
               return
          }

          context.setFilters(newFilters)
          context.setAreFiltersVisible(false)
          context.setItemInPreview(null)
     }

     function handleReset() {
          const defaultFilters: IGetConsultantsListingRequestModel = {
               page: 1,
               pageLength: 20,
               dateFiltrage: moment().format("YYYY-MM-DD"), // Default date is today
               keyword: "",
               clients: [],
               responsables: [],
               entitesRattachement: [],
               statuts: [],
               contrats: [],
               etatsConsultant: [],
          }

          // If already default, just UI reset
          if (isEqual(context.filters, defaultFilters)) {
               context.filtersFormikRef.current?.resetForm({ values: defaultFilters })
               context.simpleSearchBarFormikRef.current?.resetForm({ values: { keyword: "" } })
               context.filtersFormikRef.current?.setSubmitting(false)
               if (window.innerWidth < 992) context.setAreFiltersVisible(false)
               return
          }

          // Global update
          context.setFilters(defaultFilters)
          context.setItemInPreview(null)
          context.setAreFiltersVisible(false)

          // Local form update
          context.filtersFormikRef.current?.resetForm({ values: defaultFilters })
          context.filtersFormikRef.current?.setSubmitting(true)
          context.simpleSearchBarFormikRef.current?.resetForm({ values: { keyword: "" } })
     }

     function handleClickHideFilters() {
          context.setAreFiltersVisible(prev => !prev)
          context.setItemInPreview(prev => (prev ? { ...prev, isPreviewVisible: true } : null))
     }

     const layoutProps = {
          classNameLayout: "mb-5",
          classNameLabel: "col-lg-4 col-form-label fw-bold fs-6",
          classNameField: "col-lg-8",
     }

     return (
          <MyCard>
               <MyCard.Header
                    isCollapseIconShown={false}
                    rightSideContent={
                         <MyTooltip title={"Cacher les filtres"}>
                              <button className={"btn btn-sm btn-outline btn-outline-secondary "} onClick={handleClickHideFilters}>
                                   <i className={"fas fa-eye-slash fs-4 text-dark"} />
                              </button>
                         </MyTooltip>
                    }
               >
                    Filtres
               </MyCard.Header>

               <MyCard.Body>
                    <Formik
                         initialValues={initialValues}
                         onSubmit={handleSubmit}
                         innerRef={context.filtersFormikRef}
                         enableReinitialize={true} // 🔹 Important for Reset/Sync
                    >
                         {formikProps => {
                              return (
                                   <Form noValidate autoComplete="off">
                                        <MyInputFormField
                                             label="Mot clé"
                                             name="keyword"
                                             placeholder="Rechercher ..."
                                             formikProps={formikProps}
                                             onChangeCallback={val => {
                                                  context.simpleSearchBarFormikRef.current?.setFieldValue("keyword", val)
                                             }}
                                             {...layoutProps}
                                        />

                                        <div className="separator separator-dotted mt-4 mb-8"></div>

                                        {/* Date (Manual wrapper since no new FormField provided for Date) */}
                                        <div className="row mb-5">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6 required">Date</label>
                                             <div className="col-lg-8">
                                                  <MyDateDayMonthYearField
                                                       name={"dateFiltrage"}
                                                       value={formikProps.values.dateFiltrage}
                                                       onChange={val => formikProps.setFieldValue("dateFiltrage", val)}
                                                       isInvalid={!!formikProps.errors.dateFiltrage}
                                                       placeholder={"Date"}
                                                  />
                                                  {formikProps.errors.dateFiltrage && <div className={"text-danger"}>{formikProps.errors.dateFiltrage}</div>}
                                             </div>
                                        </div>

                                        <MySelectGetAllDataFromServerFormField
                                             label="Clients"
                                             name="clients"
                                             placeholder="Sélectionner..."
                                             endpoint={CONST_API_UTILS_GET_CLIENTS_ENDPOINT}
                                             method="GET"
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectGetAllDataFromServerFormField
                                             label="Responsables"
                                             name="responsables"
                                             placeholder="Sélectionner..."
                                             endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                             method="GET"
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectGetAllDataFromServerFormField
                                             label="Entités de rattachement"
                                             name="entitesRattachement"
                                             placeholder="Sélectionner..."
                                             endpoint={CONST_API_UTILS_GET_ENTITES_ENDPOINT}
                                             method="GET"
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectFormField
                                             label="Statuts"
                                             name="statuts"
                                             placeholder="Sélectionner..."
                                             options={CONST_CONSULTANT_STATUTS_OPTIONS_FOR_SELECT2}
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectFormField
                                             label="Contrats"
                                             name="contrats"
                                             placeholder="Sélectionner..."
                                             options={CONST_CONTRATS_FOR_SELECT2}
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectFormField
                                             label="États"
                                             name="etatsConsultant"
                                             placeholder="Sélectionner..."
                                             options={[
                                                  { value: CONST_CONSULTANT_CRA_DETAILS_ETAT_EN_MISSION, label: "En mission" },
                                                  { value: CONST_CONSULTANT_CRA_DETAILS_ETAT_INACTIF, label: "En intercontrat" },
                                             ]}
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <div className={"text-end"}>
                                             <button
                                                  className={"btn btn-sm btn-outline btn-outline-primary d-inline-flex align-items-center me-2"}
                                                  type={"button"}
                                                  disabled={formikProps.isSubmitting}
                                                  onClick={() => handleReset()}
                                             >
                                                  <span className={"d-none d-lg-block"}>Réinitialiser</span> <i className={"fas fa-sync ms-lg-1"} />
                                             </button>
                                             <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={formikProps.isSubmitting}>
                                                  <span className={"d-none d-lg-block"}>Filtrer </span>
                                                  {!formikProps.isSubmitting ? (
                                                       <i className={"fas fa-filter ms-md-1"} />
                                                  ) : (
                                                       <span className="spinner-border spinner-border-sm align-middle ms-lg-1" />
                                                  )}
                                             </button>
                                        </div>
                                   </Form>
                              )
                         }}
                    </Formik>
               </MyCard.Body>
          </MyCard>
     )
}

export default ConsultantsListingFilters
